import type { ProjectsViewFilters } from "../types";
import type { FilterOptionList, FilterOptions } from "./useFilterOptions";

/** One "Filtering By" entry: `<label> (<value>)`. */
export interface FilterSummaryItem {
  label: string;
  value: string;
}

/**
 * How many filters are applied, for the filter icon's "N Active" badge and
 * the slideout's count. My Projects and status (which drives the tab) aren't
 * counted, and tagIds shadows tags. Each date bound counts on its own.
 */
export const activeFilterCount = (f: ProjectsViewFilters): number =>
  Object.entries(f).filter(
    ([k, v]) =>
      k !== "showMyProjects" &&
      k !== "status" &&
      k !== "tagIds" &&
      (Array.isArray(v) ? v.length > 0 : !!v)
  ).length;

type ListKey =
  | "clients"
  | "salesExecs"
  | "presalesEngineers"
  | "teams"
  | "teammates"
  | "tags"
  | "rateTableIds"
  | "lobIds"
  | "businessUnits";
type DateKey =
  | "dateEditedStart"
  | "dateEditedEnd"
  | "dateCreatedStart"
  | "dateCreatedEnd";

// Legacy list order and labels.
const LISTS: {
  key: ListKey;
  label: string;
  options: (o: FilterOptions) => FilterOptionList;
}[] = [
  { key: "clients", label: "Client", options: (o) => o.clients },
  { key: "salesExecs", label: "Sales Exec", options: (o) => o.salesExecs },
  {
    key: "presalesEngineers",
    label: "Pre-Sales Eng",
    options: (o) => o.presalesEngineers,
  },
  { key: "teams", label: "Teams", options: (o) => o.teams },
  { key: "teammates", label: "Teammates", options: (o) => o.teammates },
  { key: "tags", label: "Tags", options: (o) => o.tags },
  { key: "rateTableIds", label: "Rate Table", options: (o) => o.rateTables },
  { key: "lobIds", label: "Line of Business", options: (o) => o.lobs },
  { key: "businessUnits", label: "", options: (o) => o.businessUnits },
];

const DATES: { key: DateKey; label: string }[] = [
  { key: "dateEditedStart", label: "Last Edited After" },
  { key: "dateEditedEnd", label: "Last Edited Before" },
  { key: "dateCreatedStart", label: "Created After" },
  { key: "dateCreatedEnd", label: "Created Before" },
];

// A saved "YYYY-MM-DD" is a calendar date; new Date() would read it as UTC
// midnight and show the day before in US time zones.
const formatDate = (value: string): string => {
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = ymd ? new Date(+ymd[1], +ymd[2] - 1, +ymd[3]) : new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
};

// "Acme", "Acme and 2 more", or "2 selected" when no value is in the loaded
// options (e.g. a client past the first page).
const describeValues = (values: string[], list: FilterOptionList): string => {
  const first = values
    .map((v) => list.options.find((o) => o.value === v)?.label)
    .find((label): label is string => !!label);
  if (!first) return `${values.length} selected`;
  return values.length > 1 ? `${first} and ${values.length - 1} more` : first;
};

/** The "Filtering By" entries for the applied filters, in legacy order. */
export const filterSummary = (
  f: ProjectsViewFilters,
  options: FilterOptions
): FilterSummaryItem[] => [
  ...LISTS.filter(({ key }) => (f[key]?.length ?? 0) > 0).map(
    ({ key, label, options: pick }) => ({
      label: key === "businessUnits" ? options.businessUnitLabel : label,
      value: describeValues(f[key] ?? [], pick(options)),
    })
  ),
  ...DATES.filter(({ key }) => !!f[key]).map(({ key, label }) => ({
    label,
    value: formatDate(f[key] ?? ""),
  })),
];
